import { useCallback } from "react";
import { ChoiceGroup, IChoiceGroupOption } from "@fluentui/react/lib/ChoiceGroup";
import { Stack } from "@fluentui/react/lib/Stack";
import { Text } from "@fluentui/react/lib/Text";
import { TooltipHost, TooltipOverflowMode } from "@fluentui/react/lib/Tooltip";

import { WithTranslator } from "Components/Generic/Translator";
import { getFormattedValue, getTranslatedFieldLabel } from "Utils/Field";
import { WithConfiguration } from "Components/Generic/Configuration";
import { ValueWithTooltip } from "Components/Generic/ValueWithTooltip";
import { IItem } from "Model/Storage/IItem";
import { isNotNullOrWhiteSpace } from "Utils/String";
import { IMergeEntryProps } from "./IMergeEntryProps";
import { useMergeEntryStyles } from "./UseMergeEntryStyles";

export const MergeEntry = WithConfiguration(
  WithTranslator((props: IMergeEntryProps): JSX.Element => {
    const {
      allowDisableValue = true,
      config,
      fieldName,
      index,
      entityType,
      onChange,
      selectedKey,
      sourceItem,
      targetItem,
      translator
    } = props;

    const { styles, valueStyles } = useMergeEntryStyles();

    const meta = config.getMetadata(fieldName, entityType);
    const sourceValue = sourceItem?.[fieldName];
    const targetValue = targetItem?.[fieldName];

    const onSelect = useCallback(
      (_, choice: IChoiceGroupOption) => {
        const value = choice.key.includes(`source`) ? sourceValue : targetValue;
        onChange?.(choice.key, value);
      },
      [onChange, sourceValue, targetValue]
    );

    const generateOnRenderLabel = useCallback(
      (item: IItem) => () => <ValueWithTooltip entityType={entityType} fieldName={fieldName} styleSet={valueStyles} item={item} />,
      [entityType, fieldName, valueStyles]
    );

    return (
      <Stack horizontal verticalAlign="center" className={index % 2 !== 0 ? styles.root : styles.rootAlt}>
        <Stack.Item disableShrink className={styles.label}>
          <TooltipHost overflowMode={TooltipOverflowMode.Parent} content={fieldName}>
            <Text title={`${getTranslatedFieldLabel(fieldName, entityType, translator)} (${fieldName})`}>
              {getTranslatedFieldLabel(fieldName, entityType, translator)}
            </Text>
          </TooltipHost>
        </Stack.Item>
        <ChoiceGroup
          className={styles.choiceGroup}
          selectedKey={`${isNotNullOrWhiteSpace(selectedKey) ? selectedKey : `source`}.${fieldName}`}
          onChange={onSelect}
          options={[
            {
              key: `source.${fieldName}`,
              text: getFormattedValue(sourceItem, meta, translator, `-`),
              disabled: allowDisableValue === true,
              onRenderLabel: generateOnRenderLabel(sourceItem)
            },
            {
              key: `target.${fieldName}`,
              text: getFormattedValue(targetItem, meta, translator, `-`),
              disabled: allowDisableValue === true,
              onRenderLabel: generateOnRenderLabel(targetItem)
            }
          ]}
        />
      </Stack>
    );
  })
);
